import { Chart as ChartJS } from 'chart.js/auto'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
} from 'react-bootstrap'
import { Line } from 'react-chartjs-2'
import { BsDownload } from 'react-icons/bs' // Importa o ícone de download
import {
  FaArrowDown,
  FaArrowUp,
  FaBox,
  FaChartLine,
  FaCogs,
  FaDollarSign,
  FaFilePdf,
  FaTrophy,
  FaTruck,
} from 'react-icons/fa'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Navbar from '../../components/navbar/navbar.jsx'
import apinode from '../../services/apinode'

dayjs.extend(isSameOrBefore)

pdfMake.addVirtualFileSystem(pdfFonts)

function Financial() {
  const [marketplaceData, setMarketplaceData] = useState([])
  const [selectedAccounts, setSelectedAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasPermission, setHasPermission] = useState(true)
  const [startDate, setStartDate] = useState(
    dayjs().startOf('month').format('YYYY-MM-DD'),
  )
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [summary, setSummary] = useState({
    totalGrossRevenue: 0,
    totalNetRevenue: 0,
    totalOrders: 0,
    totalCanceledValue: 0,
    totalFreightValue: 0,
  })
  const [responseData, setResponseData] = useState([])

  const toggleSelection = (apelido) => {
    setSelectedAccounts((prev) =>
      prev.includes(apelido)
        ? prev.filter((item) => item !== apelido)
        : [...prev, apelido],
    )
  }

  const [categoriaSelecionada, setCategoriaSelecionada] = useState('Todas')

  const categorias = [
    'Todas',
    ...new Set(responseData.map((item) => item.categoria)),
  ]

  const dadosFiltrados = useMemo(() => {
    return categoriaSelecionada === 'Todas'
      ? responseData
      : responseData.filter((item) => item.categoria === categoriaSelecionada)
  }, [categoriaSelecionada, responseData])

  const gerarPDF = () => {
    // Ordena os dados por data (mais recentes primeiro)
    const dadosOrdenados = [...dadosFiltrados].sort(
      (a, b) => new Date(b.data) - new Date(a.data),
    )

    // Agrupa os dados por conta (apelido)
    const groupedData = dadosOrdenados.reduce((acc, item) => {
      if (!acc[item.apelido]) {
        acc[item.apelido] = { total: 0, items: [] }
      }
      acc[item.apelido].items.push(item)
      acc[item.apelido].total += item.valor_novo
      return acc
    }, {})

    const docDefinition = {
      pageOrientation: 'landscape',
      pageSize: 'A4',
      pageMargins: [20, 20, 20, 20],

      content: [
        { text: 'Relatório de Vendas', style: 'header' },
        ...Object.entries(groupedData).flatMap(([apelido, group]) => [
          { text: `Conta: ${apelido}`, style: 'subheader' },
          {
            table: {
              headerRows: 1,
              widths: [50, 40, '*', 40, 40, 50, 50],
              body: [
                [
                  { text: 'Conta', bold: true, fontSize: 8 },
                  { text: 'SKU', bold: true, fontSize: 8 },
                  { text: 'Descrição', bold: true, fontSize: 8 },
                  { text: 'V. Unitário', bold: true, fontSize: 8 },
                  { text: 'Qtd', bold: true, fontSize: 8 },
                  { text: 'Total', bold: true, fontSize: 8 },
                  { text: 'Data', bold: true, fontSize: 8 },
                ],
                ...group.items.map((item) => [
                  { text: item.apelido, fontSize: 6 },
                  { text: item.sku, fontSize: 6 },
                  { text: item.descricao, fontSize: 6, noWrap: true },
                  {
                    text: `R$ ${item?.valorunitario?.toFixed(2)}`,
                    fontSize: 6,
                    alignment: 'right',
                  },
                  {
                    text: item?.quantidade_nova?.toFixed(2),
                    fontSize: 6,
                    alignment: 'center',
                  },
                  {
                    text: `R$ ${item?.valor_novo?.toFixed(2)}`,
                    fontSize: 6,
                    alignment: 'right',
                  },
                  {
                    text: new Date(item?.data)?.toLocaleDateString('pt-BR'),
                    fontSize: 6,
                    alignment: 'center',
                  },
                ]),
                [
                  {
                    text: 'Subtotal',
                    colSpan: 5,
                    alignment: 'right',
                    bold: true,
                    fontSize: 7,
                  },
                  '',
                  '',
                  '',
                  '',
                  {
                    text: `R$ ${group?.total?.toFixed(2)}`,
                    bold: true,
                    fontSize: 7,
                    alignment: 'right',
                  },
                  '',
                ],
              ],
            },
            layout: 'lightHorizontalLines',
            margin: [0, 3, 0, 10],
          },
        ]),
        {
          text: `Total Geral: R$ ${dadosOrdenados.reduce((acc, item) => acc + item.valor_novo, 0)?.toFixed(2)}`,
          style: 'total',
        },
      ],

      styles: {
        header: { fontSize: 12, bold: true, margin: [0, 0, 0, 5] },
        subheader: { fontSize: 8, bold: true, margin: [0, 5, 0, 2] },
        total: {
          fontSize: 10,
          bold: true,
          margin: [0, 10, 0, 0],
          color: 'blue',
        },
      },

      defaultStyle: {
        fontSize: 6,
        columnGap: 2,
      },
    }

    pdfMake.createPdf(docDefinition).open()
  }

  const data = null
  const generatePDF = () => {
    const filteredData = marketplaceData.filter((item) =>
      selectedAccounts.includes(item.apelido),
    )

    // Cálculo dos subtotais
    const totalFaturamentoBruto = filteredData.reduce(
      (sum, item) => sum + item.faturamento_bruto,
      0,
    )
    const totalFaturamentoLiquido = filteredData.reduce(
      (sum, item) => sum + item.faturamento_liquido,
      0,
    )
    const totalCancelado = filteredData.reduce(
      (sum, item) => sum + item.total_cancelado,
      0,
    )
    const totalPedidos = filteredData.reduce(
      (sum, item) => sum + item.total_pedidos,
      0,
    )

    const docDefinition = {
      content: [
        { text: 'Relatório de Vendas Ziphub por Marketplace', style: 'header' },
        {
          text: `Período: ${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`,
          style: 'subheader',
          margin: [0, 5, 0, 10],
        },
        {
          table: {
            headerRows: 1,
            widths: ['auto', '*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Marketplace', style: 'tableHeader' },
                { text: 'Faturamento Bruto', style: 'tableHeader' },
                { text: 'Faturamento Líquido', style: 'tableHeader' },
                { text: 'Pedidos Cancelados', style: 'tableHeader' },
                { text: 'Total Pedidos', style: 'tableHeader' },
              ],
              ...filteredData.map((item) => [
                { text: item.apelido, style: 'tableCell' },
                {
                  text: item.faturamento_bruto.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
                  style: 'tableCell',
                },
                {
                  text: item.faturamento_liquido.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
                  style: 'tableCell',
                },
                {
                  text: item.total_cancelado.toLocaleString('pt-BR'),
                  style: 'tableCell',
                },
                {
                  text: item.total_pedidos.toLocaleString('pt-BR'),
                  style: 'tableCell',
                },
              ]),
              // Linha de subtotais
              [
                { text: 'Subtotal', style: 'tableHeader', colSpan: 1 },
                {
                  text: totalFaturamentoBruto.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
                  style: 'tableCell',
                },
                {
                  text: totalFaturamentoLiquido.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
                  style: 'tableCell',
                },
                {
                  text: totalCancelado.toLocaleString('pt-BR'),
                  style: 'tableCell',
                },
                {
                  text: totalPedidos.toLocaleString('pt-BR'),
                  style: 'tableCell',
                },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          alignment: 'center',
          marginBottom: 10,
        },
        subheader: { fontSize: 12, alignment: 'center', italics: true },
        tableHeader: {
          bold: true,
          fontSize: 12,
          fillColor: '#f3f3f3',
          alignment: 'center',
        },
        tableCell: { fontSize: 10, margin: [3, 3, 3, 3], alignment: 'center' },
      },
    }

    pdfMake.createPdf(docDefinition).open()
  }

  const groupedData = useMemo(() => {
    return dadosFiltrados.reduce((acc, item) => {
      if (!acc[item.apelido]) {
        acc[item.apelido] = { total: 0, items: [] }
      }
      acc[item.apelido].items.push(item)
      acc[item.apelido].total += item.valor_novo
      return acc
    }, {})
  }, [dadosFiltrados])

  const fetchData = async (start, end) => {
    setLoading(true)
    const url = `faturamento/${localStorage.idconta}?data1=${dayjs(start).format('DD.MM.YYYY')}&data2=${dayjs(end).format('DD.MM.YYYY')}`
    const urlCategoria = `relatorio/categoria/${localStorage.idconta}`

    try {
      const responseCategoria = await apinode.get(urlCategoria, {
        params: {
          data1: dayjs(start).format('DD.MM.YYYY'),
          data2: dayjs(end).format('DD.MM.YYYY'),
        },
      })

      setResponseData(responseCategoria.data) // Agora está no local correto
      console.log(responseCategoria.data)
    } catch (error) {
      console.error('Erro ao buscar categorias:', error)
    }

    //console.log(responseCategoria)

    try {
      const response = await apinode.get(url)
      const dataWithNetRevenue = response.data.map((item) => ({
        ...item,
        faturamento_liquido: item.faturamento_bruto - item.valor_frete, // Cálculo do faturamento líquido
        ticket_medio: item.faturamento_bruto / (item.total_pedidos || 1), // Cálculo do ticket médio
      }))

      const sortedData = dataWithNetRevenue.sort(
        (a, b) => b.faturamento_liquido - a.faturamento_liquido,
      )

      // Calculando os resumos
      const totalGrossRevenue = dataWithNetRevenue.reduce(
        (sum, item) => sum + item.faturamento_bruto,
        0,
      )
      const totalNetRevenue = dataWithNetRevenue.reduce(
        (sum, item) => sum + item.faturamento_liquido,
        0,
      )
      const totalOrders = dataWithNetRevenue.reduce(
        (sum, item) => sum + item.total_pedidos,
        0,
      )
      const totalCanceledValue = dataWithNetRevenue.reduce(
        (sum, item) => sum + item.valor_cancelado,
        0,
      )
      const totalFreightValue = dataWithNetRevenue.reduce(
        (sum, item) => sum + item.valor_frete,
        0,
      )

      setMarketplaceData(sortedData)
      setSummary({
        totalGrossRevenue,
        totalNetRevenue,
        totalOrders,
        totalCanceledValue,
        totalFreightValue,
      })
    } catch (error) {
      console.error('Erro ao buscar dados da API:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const isAdmin = localStorage.getItem('admin')
  
    if (isAdmin !== 'SIM') {
      setHasPermission(false)
  
      toast.error('Você não tem permissão para acessar esta tela!', {
        position: 'top-center',
        autoClose: 2000,
      })
  
      setTimeout(() => {
        window.location.href = '/'
      }, 2000)
  
      return
    }
  
    // Se quiser carregar dados iniciais sem filtro
    fetchData()
  }, []) // <<< vazio, roda só 1 vez na montagem
  

  const handleSearch = () => {
    fetchData(startDate, endDate)
  }

  // Gráfico de Cancelamentos
  const cancelChartData = {
    labels: marketplaceData.map((item) => item.apelido),
    datasets: [
      {
        label: 'Valor Cancelado',
        data: marketplaceData.map((item) => item.valor_cancelado),
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        fill: true,
      },
    ],
  }

  // Gráfico de Pedidos Full
  const fullOrderChartData = {
    labels: marketplaceData.map((item) => item.apelido),
    datasets: [
      {
        label: 'Pedidos Full',
        data: marketplaceData.map((item) => item.total_fullfilment),
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        fill: true,
      },
    ],
  }

  return (
    <>
      <Navbar />
      <Container className="mt-page">
        <Row className="mb-3">
          <Col md={4} sm={12}>
            <Form.Group controlId="startDate">
              <Form.Label>Data de Início</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)} // só atualiza state
              />
            </Form.Group>
          </Col>

          <Col md={4} sm={12}>
            <Form.Group controlId="endDate">
              <Form.Label>Data de Fim</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)} // só atualiza state
              />
            </Form.Group>
          </Col>

          <Col
            md={4}
            sm={12}
            className="d-flex align-items-end mt-2 mt-md-0"
          >
            <Button
              variant="primary"
              onClick={() => handleSearch(startDate, endDate)} // só dispara no clique
              className="px-4"
            >
              Pesquisar
            </Button>
          </Col>
        </Row>


        {/* Card Contêiner para os Resumos */}
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Resumo de Faturamento</Card.Title>
            <Row>
              <Col md={3}>
                <Card
                  className="text-center"
                  style={{ minHeight: '180px', backgroundColor: '#f8f9fa' }}
                >
                  <Card.Body>
                    <FaDollarSign
                      className="text-primary mb-2"
                      size={30}
                    />
                    <Card.Title>Faturamento Bruto</Card.Title>
                    <Card.Text>
                      R${' '}
                      {summary.totalGrossRevenue.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      })}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="text-center"
                  style={{ minHeight: '180px', backgroundColor: '#f8f9fa' }}
                >
                  <Card.Body>
                    <FaChartLine
                      className="text-success mb-2"
                      size={30}
                    />
                    <Card.Title>Faturamento Líquido</Card.Title>
                    <Card.Text>
                      R${' '}
                      {summary.totalNetRevenue.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      })}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="text-center"
                  style={{ minHeight: '180px', backgroundColor: '#f8f9fa' }}
                >
                  <Card.Body>
                    <FaBox
                      className="text-info mb-2"
                      size={30}
                    />
                    <Card.Title>Total de Pedidos</Card.Title>
                    <Card.Text>{summary.totalOrders}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="text-center"
                  style={{ minHeight: '180px', backgroundColor: '#f8f9fa' }}
                >
                  <Card.Body>
                    <FaTruck
                      className="text-warning mb-2"
                      size={30}
                    />
                    <Card.Title>Total de Frete</Card.Title>
                    <Card.Text>
                      R${' '}
                      {summary.totalFreightValue.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      })}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="text-center"
                  style={{ minHeight: '180px', backgroundColor: '#f8f9fa' }}
                >
                  <Card.Body>
                    <FaArrowDown
                      className="text-danger mb-2"
                      size={30}
                    />
                    <Card.Title>Valor Cancelado</Card.Title>
                    <Card.Text>
                      R${' '}
                      {summary.totalCanceledValue.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      })}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>

              {dayjs(startDate).isSame(dayjs().startOf('month'), 'day') &&
                dayjs(endDate).isSameOrBefore(
                  dayjs().endOf('month'),
                  'day',
                ) && (
                  <Col md={3}>
                    <Card
                      className="text-center"
                      style={{ minHeight: '180px', backgroundColor: '#f8f9fa' }}
                    >
                      <Card.Body>
                        <FaTrophy
                          className="text-info mb-2"
                          size={30}
                        />
                        <Card.Title>Faturamento Projetado</Card.Title>
                        <Card.Text>
                          R${' '}
                          {(() => {
                            const today = dayjs()
                            const totalDiasMes = today.daysInMonth()
                            const diaAtual = today.date()

                            const mediaDiaria =
                              diaAtual > 0
                                ? summary.totalNetRevenue / diaAtual
                                : 0

                            const projecao = mediaDiaria * totalDiasMes

                            return projecao.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                            })
                          })()}
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
            </Row>
          </Card.Body>
        </Card>
        {/* Card para a Tabela */}
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Marketplace</Card.Title>
            <Row>
              {loading ? (
                <Col>
                  <div className="text-center">
                    <h4>Carregando dados...</h4>
                  </div>
                </Col>
              ) : (
                <Col>
                  <Table
                    striped
                    bordered
                    hover
                    responsive
                  >
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Marketplace</th>
                        <th>Faturamento Bruto</th>
                        <th>Valor Frete</th>
                        <th>Faturamento Líquido</th>
                        <th>Fat. Parts</th>
                        <th>Ticket Médio</th>
                        <th>Total Fullfilment</th>
                        <th>Total Pedidos</th>
                        <th>% Fullfilment</th>
                        <th>Valor Cancelado</th>
                        <th>Total Cancelado</th>
                        <th>% Cancelado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketplaceData.length > 0 ? (
                        marketplaceData.map((item, index) => (
                          <tr
                            key={index}
                            className={index === 0 ? 'table-success' : ''}
                          >
                            <td>
                              <Badge bg={index === 0 ? 'warning' : 'secondary'}>
                                {index + 1}
                              </Badge>
                            </td>
                            <td>{item.apelido}</td>
                            <td>
                              {item.faturamento_bruto?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td>
                              {item.valor_frete?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td>
                              {item.faturamento_liquido?.toLocaleString(
                                'pt-BR',
                                {
                                  minimumFractionDigits: 2 || 0.0,
                                },
                              )}
                            </td>
                            <td>
                              {item.valor_parts?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td>
                              {item.ticket_medio?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td>
                              {item.total_fullfilment?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td>{item.total_pedidos}</td>
                            <td>{item.percentual_fullfilment?.toFixed(2)}%</td>
                            <td>
                              {item.valor_cancelado?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td>{item.total_cancelado}</td>
                            <td>{item.percentual_cancelado?.toFixed(2)}%</td>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedAccounts.includes(
                                  item.apelido,
                                )}
                                onChange={() => toggleSelection(item.apelido)}
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="13"
                            className="text-center"
                          >
                            Nenhum dado encontrado para o período selecionado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  <Button
                    onClick={generatePDF}
                    disabled={selectedAccounts.length === 0}
                  >
                    <FaFilePdf /> Gerar PDF
                  </Button>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>
        {/* Gráficos */}
        <Row className="mt-4">
          <Col md={6}>
            <Card>
              <Card.Body>
                <Card.Title>Cancelamentos</Card.Title>
                <div style={{ height: '300px' }}>
                  <Line data={cancelChartData} />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Body>
                <Card.Title>Pedidos Full</Card.Title>
                <div style={{ height: '300px' }}>
                  <Line data={fullOrderChartData} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="mt-4 d-flex justify-content-center">
          <Card
            className="mb-4 shadow-lg w-100 rounded-4 border-0"
            style={{ maxWidth: '1200px' }}
          >
            <Card.Header
              className="bg-gradient text-white text-center fs-5 fw-bold border-0 py-3"
              style={{ background: '#2C3E50' }}
            >
              📊 Categoria - Relatório de Vendas
            </Card.Header>

            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  Selecionar Categoria:
                </Form.Label>
                <Form.Select
                  value={categoriaSelecionada}
                  onChange={(e) => setCategoriaSelecionada(e.target.value)}
                >
                  {categorias.map((cat, index) => (
                    <option
                      key={index}
                      value={cat}
                    >
                      {cat}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <div className="table-responsive">
                <table className="table table-hover align-middle text-center">
                  <thead
                    className="text-white"
                    style={{ background: '#34495E' }}
                  >
                    <tr>
                      <th>Conta</th>
                      <th>SKU</th>
                      <th>Descrição</th>
                      <th>V. Unitário</th>
                      <th>Qtd</th>
                      <th>Total</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedData).map(
                      ([apelido, group], index) => (
                        <React.Fragment key={index}>
                          {group.items
                            .slice()
                            .sort((a, b) => new Date(b.data) - new Date(a.data))
                            .map((item, idx) => (
                              <tr
                                key={idx}
                                className="fw-medium"
                              >
                                <td>{item.apelido}</td>
                                <td>{item.sku}</td>
                                <td className="text-start">{item.descricao}</td>
                                <td className="text-success fw-bold">
                                  R$ {item?.valorunitario?.toFixed(2)}
                                </td>
                                <td>
                                  {Number(item?.quantidade_nova)?.toFixed(2)}
                                </td>
                                <td className="text-primary fw-bold">
                                  R$ {item?.valor_novo?.toFixed(2)}
                                </td>
                                <td className="text-muted">
                                  {new Date(item.data).toLocaleDateString(
                                    'pt-BR',
                                  )}
                                </td>
                              </tr>
                            ))}
                          <tr className="fw-bold text-danger">
                            <td
                              colSpan="5"
                              className="text-end pe-3"
                            >
                              Subtotal ({apelido}):
                            </td>
                            <td className="text-danger fs-5">
                              R$ {group?.total?.toFixed(2)}
                            </td>
                            <td></td>
                          </tr>
                        </React.Fragment>
                      ),
                    )}
                  </tbody>
                  <tfoot className="fw-bold text-dark bg-light">
                    <tr>
                      <td
                        colSpan="5"
                        className="text-end pe-3"
                      >
                        Total das Vendas:
                      </td>
                      <td className="text-primary fs-5">
                        R${' '}
                        {useMemo(
                          () =>
                            dadosFiltrados
                              .reduce((acc, item) => acc + item.valor_novo, 0)
                              ?.toFixed(2),
                          [dadosFiltrados],
                        )}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
                <Button
                  className="mb-3 d-flex align-items-center gap-1 btn-sm"
                  variant="success"
                  style={{ padding: '4px 8px', fontSize: '12px' }}
                  onClick={gerarPDF}
                >
                  <BsDownload size={12} /> Baixar PDF
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Row>
        ;
      </Container>
    </>
  )
}

export default Financial
